import { useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import type { PassViewRecord } from '../../../utils/fetchWeldViewApis';
import { deriveBinaryPassStatus } from '../../../utils/passViewFormatters';
import type { WpsViolationRecord } from '../../../utils/wpsViolationModel';
import { PassSummaryDrillDownHost } from './PassSummaryDrillDownHost';

interface ProductionPassSummaryTableProps {
  passes: PassViewRecord[];
  loading?: boolean;
  statusCalculationMethod?: string;
  /** When true, highlight passes that appear in `violations`. */
  outOfLimitContext?: boolean;
  violations?: WpsViolationRecord[];
  limitsConfigured?: boolean;
  weldNumber?: string;
  jobNumber?: string;
  /** Controlled open state for the pass-summary dialog. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function demoPasses(jobNumber: string): PassViewRecord[] {
  return ['Root', 'Hot', 'Fill', 'Cap'].map((name) => ({
    id: `demo-${name.toLowerCase()}`,
    name,
    jobNumber,
    welderId: '—',
    status: 'Completed',
    params: {},
  }));
}

export const ProductionPassSummaryTable = ({
  passes,
  loading = false,
  statusCalculationMethod = 'Instantaneous',
  outOfLimitContext = false,
  violations = [],
  limitsConfigured = true,
  weldNumber = '',
  jobNumber = '',
  open: openProp,
  onOpenChange,
}: ProductionPassSummaryTableProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;
  const rows = passes.length > 0 ? passes : demoPasses(jobNumber);

  const { failedCount, passedCount } = useMemo(() => {
    let failed = 0;
    let passed = 0;
    rows.forEach((pass) => {
      if (deriveBinaryPassStatus(pass) === 'Failed') failed += 1;
      else passed += 1;
    });
    return { failedCount: failed, passedCount: passed };
  }, [rows]);

  if (loading) {
    return <p className="text-sm text-heritage-grey">Loading pass summary…</p>;
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E2E5EA] bg-white px-3 py-3">
        <div>
          <p className="text-sm font-medium text-blueprint-blue">
            {rows.length} pass{rows.length === 1 ? '' : 'es'}
          </p>
          <p className="text-xs text-heritage-grey">
            {passedCount} passed · {failedCount} failed
            {outOfLimitContext
              ? ' — open for filters, sorts, and per-pass WPS limits'
              : ' — open for filters, sorts, and pass detail'}
          </p>
        </div>
        <button
          type="button"
          data-testid="open-pass-summary"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#E2E5EA] bg-white px-3 py-2 text-xs font-semibold text-blueprint-blue hover:border-blueprint-blue"
        >
          Open pass summary
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>

      <PassSummaryDrillDownHost
        open={open}
        onOpenChange={setOpen}
        weldNumber={weldNumber}
        jobNumber={jobNumber}
        passes={rows}
        loading={loading}
        statusCalculationMethod={statusCalculationMethod}
        outOfLimitContext={outOfLimitContext}
        violations={violations}
        limitsConfigured={limitsConfigured}
      />
    </div>
  );
};
